import { createContext, useContext, ReactNode } from "react";
import { useVegetableOils } from "@/hooks/useVegetableOils";
import { useNeutralBases } from "@/hooks/useNeutralBases";
import { useSupplies } from "@/hooks/useSupplies";
import { useHydrolats } from "@/hooks/useHydrolats";
import { Product } from "@/types/product";

interface ProductsContextType {
  vegetableOils: Product[];
  neutralBases: Product[];
  supplies: Product[];
  hydrolats: Product[];
  allProducts: Product[];
  vegetableOilCategories: string[];
  neutralBaseCategories: string[];
  supplyCategories: string[];
  hydrolatCategories: string[];
  loading: boolean;
  searchVegetableOils: (query: string) => Product[];
  searchNeutralBases: (query: string) => Product[];
  searchSupplies: (query: string) => Product[];
  searchHydrolats: (query: string) => Product[];
  getProductById: (id: string) => Product | undefined;
  refetchAll: () => Promise<void>;
}

const ProductsContext = createContext<ProductsContextType | undefined>(undefined);

export function ProductsProvider({ children }: { children: ReactNode }) {
  const vo = useVegetableOils();
  const nb = useNeutralBases();
  const sp = useSupplies();
  const hy = useHydrolats();

  const allProducts = [...vo.products, ...nb.products, ...sp.products, ...hy.products];
  const loading = vo.loading || nb.loading || sp.loading || hy.loading;

  const getProductById = (id: string) => allProducts.find(p => p.id === id);

  const refetchAll = async () => {
    await Promise.all([vo.refetch(), nb.refetch(), sp.refetch(), hy.refetch()]);
  };

  return (
    <ProductsContext.Provider value={{
      vegetableOils: vo.products,
      neutralBases: nb.products,
      supplies: sp.products,
      hydrolats: hy.products,
      allProducts,
      vegetableOilCategories: vo.categories,
      neutralBaseCategories: nb.categories,
      supplyCategories: sp.categories,
      hydrolatCategories: hy.categories,
      loading,
      searchVegetableOils: vo.searchProducts,
      searchNeutralBases: nb.searchProducts,
      searchSupplies: sp.searchProducts,
      searchHydrolats: hy.searchProducts,
      getProductById,
      refetchAll,
    }}>
      {children}
    </ProductsContext.Provider>
  );
}

export function useProductsContext() {
  const context = useContext(ProductsContext);
  if (context === undefined) {
    throw new Error("useProductsContext must be used within a ProductsProvider");
  }
  return context;
}

export type { Product };
